import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  KeyboardSensor,
  PointerSensor
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  arrayMove
} from '@dnd-kit/sortable'

import {
  uiSetOrderedDeliveryTableColumns,
  uiSetOrderedWasteTableColumns,
  uiSetShownDeliveryTableColumns,
  uiSetShownWasteTableColumns
} from 'portal/store/ui/actions'

import { TableSettingsSortableItem } from './TableSettingsSortableItem'

type Item = { id: string; title: string }

export const TableSettings = ({ moduleName }) => {
  const [items, setItems] = useState<Item[]>([])

  const dispatch = useDispatch()
  const allColumns = useSelector(
    (state: any) => state.ui.data[`${moduleName.toLowerCase()}TableColumns`]
  )
  const shownTableColumns = useSelector(
    (state: any) =>
      state.ui.data[`${moduleName.toLowerCase()}ShownTableColumns`]
  )

  // Initialize items from localStorage if present, otherwise use allColumns
  useEffect(() => {
    const storedItems = localStorage.getItem(
      `${moduleName.toLowerCase()}-orderedTableColumns`
    )

    if (storedItems) {
      const parsedItems = JSON.parse(storedItems)
      // Add titles to parsedItems based on allColumns
      const itemsWithTitles = parsedItems
        .map(id => {
          const col = allColumns.find(c => c.dataIndex === id)
          return col ? { id: col.dataIndex, title: col.title } : null
        })
        .filter(item => item !== null) // Remove any nulls (in case of mismatch)

      // Expect itemsWithTitles length to match allItems length, if not add missing columns at the end
      const missingColumns = allColumns.filter(
        col => !itemsWithTitles.find(item => item.id === col.dataIndex)
      )
      missingColumns.forEach(col => {
        itemsWithTitles.push({ id: col.dataIndex, title: col.title })
      })
      setItems(itemsWithTitles)
    } else {
      setItems(
        allColumns.map(col => ({
          id: col.dataIndex,
          title: col.title
        }))
      )
    }
  }, [allColumns, moduleName])

  const handleTableSettingsColumnChange = (e, col) => {
    const currentShownColumns = [...shownTableColumns]
    let newShownColumns: string[] = []
    if (e.target.checked) {
      // Add column to shown columns
      newShownColumns = [...currentShownColumns, col.dataIndex]
    } else {
      // Remove column from shown columns
      newShownColumns = currentShownColumns.filter(c => c !== col.dataIndex)
    }
    dispatch(
      moduleName.toLowerCase() === 'delivery'
        ? uiSetShownDeliveryTableColumns(newShownColumns)
        : uiSetShownWasteTableColumns(newShownColumns)
    )

    // Persist to localStorage
    localStorage.setItem(
      `${moduleName.toLowerCase()}-shownTableColumns`,
      JSON.stringify(newShownColumns)
    )
    window.dispatchEvent(new Event('storage'))
  }

  // Configure sensors for drag-and-drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  const handleDragEnd = event => {
    const { active, over } = event

    if (active.id !== over.id) {
      // Update the items state
      setItems(prevItems => {
        const oldIndex = prevItems.findIndex(item => item.id === active.id)
        const newIndex = prevItems.findIndex(item => item.id === over.id)

        const newOrder = arrayMove(prevItems, oldIndex, newIndex)

        // Persist the new order to localStorage and dispatch to Redux
        localStorage.setItem(
          `${moduleName.toLowerCase()}-orderedTableColumns`,
          JSON.stringify(newOrder.map(item => item.id))
        )
        dispatch(
          moduleName.toLowerCase() === 'delivery'
            ? uiSetOrderedDeliveryTableColumns(newOrder.map(item => item.id))
            : uiSetOrderedWasteTableColumns(newOrder.map(item => item.id))
        )
        return newOrder
      })
    }
  }

  return (
    <div>
      <p>Select and order the columns</p>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={items.map(item => item.id)}>
          {items.map(item => (
            <TableSettingsSortableItem
              key={item.id}
              id={item.id}
              title={item.title}
              shownTableColumns={shownTableColumns}
              handleTableSettingsColumnChange={handleTableSettingsColumnChange}
            />
          ))}
        </SortableContext>
      </DndContext>
    </div>
  )
}
